import {Choice, Narration, Narrator} from "../../../feature/project/Shot";
import {
    Avatar,
    Button,
    Checkbox,
    Flex,
    Image,
    InputNumber,
    Modal,
    Space,
    Typography
} from "antd";
import React, {useCallback, useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../../feature/tools";
import {useParams} from "react-router-dom";
import LayerList from "../../common/LayerList";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faGear, faPlus} from "@fortawesome/free-solid-svg-icons";
import TextArea from "antd/es/input/TextArea";
import {getFontWeight} from "./ShotViewport";
import {deleteNarrator, setNarrator} from "../../../feature/project/narrationSlice";
import {NarratorManagerDialog} from "./NarratorManagerDialog";
import {ChoiceEditDialog} from "./ChoiceEditDialog";
import {deleteChoice, saveChoice} from "../../../feature/project/choiceSlice";

const {confirm} = Modal;

export const NarrationEditModal = (
    props: {
        narration: Narration,
        onSave: (narration: Narration) => void,
        onCancel: () => void
    }
) => {
    const dispatch = useAppDispatch()
    const [hasChanged, setHasChanged] = React.useState(false)
    const {projectId} = useParams()
    const [text, setText] = React.useState(props.narration.text)
    const [continuation, setContinuation] = React.useState(props.narration.continue ?? false)
    const [delay, setDelay] = React.useState<number | undefined | null>(props.narration.delay)
    const configNarration = useAppSelector(state => state.projects.configNarration[0])
    const [width, setWidth] = useState(0)
    const [height, setHeight] = useState(0)
    const [scale, setScale] = useState(1)
    const [showSelectNarratorDialog, setShowSelectNarratorDialog] = useState(false)
    const [showAddChoiceDialog, setShowAddChoiceDialog] = useState<Choice | undefined>(undefined)

    useEffect(() => {
        setText(props.narration.text)
        setContinuation(props.narration.continue ?? false)
        setDelay(props.narration.delay)
        if (showAddChoiceDialog) {
            const choice = {
                ...props.narration?.choices?.find((choice: Choice) => choice.id === showAddChoiceDialog?.id),
            } as Choice
            setShowAddChoiceDialog(choice)
        }
    }, [props.narration])

    useEffect(() => {
        setHasChanged(text !== props.narration.text || continuation !== (props.narration.continue ?? false) || delay !== props.narration.delay)
    }, [text, continuation, delay])

    useEffect(() => {
        const nWidth = 1920 - configNarration.x - (1920 - configNarration.width - configNarration.x) + 120
        setScale(width > height ? width / nWidth : height / nWidth)
    }, [width, height, props.narration])

    const elementRef = useCallback((node: HTMLDivElement) => {
        if (!node) return;
        const resizeObserver = new ResizeObserver(() => {
            setWidth(node.clientWidth || 0)
            setHeight(node.clientHeight || 0)
        });
        resizeObserver.observe(node);
    }, []);

    const showConfirmNarratorDeletion = (item: Narration) => {
        confirm({
            title: 'Delete Narrator',
            content: 'Are you sure you want to delete this narrator?',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (projectId && item.id) {
                    dispatch(deleteNarrator({projectId: parseInt(projectId), narrationId: item.id}))
                }
            },
        });
    }


    const showConfirmChoiceDeletion = (item: Choice) => {
        confirm({
            title: 'Delete Choice',
            content: 'Are you sure you want to delete this choice?',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (projectId && item.id) {
                    dispatch(deleteChoice({projectId: parseInt(projectId), choiceId: item.id}))
                }
            },
        });
    }

    return (
        <>
            <Modal
                open={true}
                title={props.narration.id ? 'Edit Narration' : 'Add Narration'}
                width={1000}
                okText={'Save'}
                cancelText={'Close'}
                onCancel={props.onCancel}
                okButtonProps={
                    {
                        disabled: !hasChanged
                    }
                }
                onOk={() => props.onSave(
                    {
                        ...props.narration,
                        text: text,
                        continue: continuation,
                        delay: delay,
                        narrator: {
                            ...props.narration.narrator
                        },
                    } as Narration //TODO
                )}
            >
                <Flex gap={16}>
                    <Flex ref={elementRef} align={'center'} style={{
                        minWidth: 400,
                        height: 400,
                        borderRadius: 6,
                        clipPath: 'border-box',
                        position: "relative",
                        background: '#eeeeee'
                    }}>
                        <div
                            style={{
                                position: 'absolute',
                                top: `calc(50% - ${(props.narration.height ?? configNarration.height) * scale / 2}px)`,
                                left: `calc(50% - ${(props.narration.width ?? configNarration.width) * scale / 2}px)`,
                                borderImageSource: `url(${configNarration.background_url})`,
                                borderImageSlice: `${configNarration.background_nine_slice_top} ${configNarration.background_nine_slice_right} ${configNarration.background_nine_slice_bottom} ${configNarration.background_nine_slice_left} fill`,
                                borderImageWidth: `${configNarration.background_nine_slice_top}px ${configNarration.background_nine_slice_right}px ${configNarration.background_nine_slice_bottom}px ${configNarration.background_nine_slice_left}px`,
                                zIndex: configNarration.z,
                                width: `${props.narration.width ?? configNarration.width}px`,
                                height: `${props.narration.height ?? configNarration.height}px`,
                                scale: `${scale}`,
                                transformOrigin: 'top left'
                            }}
                        >
                            {props.narration.narrator?.icon ?
                                <img
                                    style={{
                                        position: 'absolute',
                                        width: configNarration.avatar_width,
                                        height: configNarration.avatar_height,
                                        left: `${configNarration.avatar_x}px`,
                                        top: `${configNarration.avatar_y}px`,
                                    }}
                                    src={props.narration.narrator.icon}/>
                                : ''}
                            {props.narration.narrator ?
                                <div
                                    style={{
                                        fontSize: `${configNarration.name_font_size}px`,
                                        color: `${props.narration.narrator.color ?? configNarration.name_font_color}`,
                                        fontStyle: `${configNarration.name_font_style}`,
                                        fontWeight: getFontWeight(configNarration.name_font_weight),
                                        position: 'absolute',
                                        top: `${configNarration.name_y}px`,
                                        left: `${(configNarration.name_x ?? 0) - (props.narration.narrator.icon ? 0 : ((configNarration.avatar_width ?? 0) + (configNarration.avatar_x ?? 0)))}px`,
                                    }}>{props.narration.narrator.title}</div>
                                : ''}
                            <div
                                style={{
                                    fontSize: `${configNarration.text_font_size}px`,
                                    lineHeight: `${configNarration.text_font_size}px`,
                                    position: 'absolute',
                                    color: `${configNarration.text_font_color}`,
                                    fontStyle: `${configNarration.text_font_style}`,
                                    fontWeight: getFontWeight(configNarration.text_font_weight),
                                    top: `${(configNarration.text_y ?? 0) - (props.narration.narrator ? 0 : (configNarration.name_font_size ?? 0))}px`,
                                    left: `${(configNarration.text_x ?? 0) - (props.narration.narrator?.icon ? 0 : ((configNarration.avatar_width ?? 0) + (configNarration.avatar_x ?? 0)))}px`,
                                    width: `${(props.narration.width ?? 0) - (props.narration.narrator ? (configNarration.width - (configNarration.text_width ?? 0)) : ((configNarration.text_x ?? 0) - (configNarration.avatar_width ?? 0)) )}px`,
                                    whiteSpace: 'pre-line'
                                }}>{text}</div>
                        </div>
                    </Flex>
                    <Space direction={'vertical'} style={{width: '100%', maxHeight: 400, overflowY: 'auto', scrollbarWidth: "none"}}
                           size={16}>
                        <div>
                            <Typography style={{fontWeight: 700}}>Text</Typography>
                            <TextArea
                                rows={5}
                                value={text}
                                onChange={(event) => {
                                    setText(event.target.value)
                                }}
                            />
                        </div>
                        <div>
                            <Checkbox checked={continuation}
                                      onChange={(e) => setContinuation(e.target.checked === true)}
                            >Continuation</Checkbox>
                        </div>
                        <div>
                            <InputNumber
                                controls={false}
                                value={delay}
                                addonBefore={<>Delay, ms</>}
                                onChange={(value: number | null) => {
                                    setDelay(value)
                                }}
                            />
                        </div>
                        <div>
                            <Flex justify={'space-between'} className={'layer-group-title'} align={'center'}>
                                <Typography style={{fontWeight: 700}}>Narrator</Typography>
                                <Button
                                    icon={<FontAwesomeIcon icon={faPlus}/>}
                                    type={'text'}
                                    disabled={props.narration.narrator !== undefined && props.narration.narrator !== null}
                                    onClick={() =>
                                        setShowSelectNarratorDialog(true)
                                    }
                                />
                            </Flex>
                            {props.narration.narrator ?
                                <LayerList
                                    data={props.narration.narrator ? [props.narration.narrator] : []}
                                    itemStyle={{padding: 0}}
                                    clickable={true}
                                    title={(item, index) => <Space>
                                        <Avatar shape={'square'} src={item.icon}/>
                                        <div
                                            style={{color: props.narration.narrator.color ?? configNarration.name_font_color}}>
                                            {item.title}
                                        </div>
                                    </Space>}
                                    onItemClick={(item) => {
                                        setShowSelectNarratorDialog(true)
                                    }}
                                    onDeleteClick={(item) => {
                                        showConfirmNarratorDeletion(props.narration)
                                    }}
                                    onEditClick={(item) => {
                                        setShowSelectNarratorDialog(true)
                                    }}
                                />
                                : <Flex align={'center'} justify={'center'} style={{padding: 20}}>No narrator
                                    selected</Flex>
                            }
                        </div>
                        <div>
                            <Flex justify={'space-between'} className={'layer-group-title'} align={'center'}>
                                <Typography style={{fontWeight: 700}}>Choice</Typography>
                                <Space>
                                    <Button
                                        icon={<FontAwesomeIcon icon={faGear}/>}
                                        type={'text'}
                                        onClick={() => {
                                            //TODO show settings columns: number, width: number, height: number
                                        }}
                                    />
                                    <Button
                                        icon={<FontAwesomeIcon icon={faPlus}/>}
                                        type={'text'}
                                        onClick={() =>
                                            setShowAddChoiceDialog({actions: [], title: ""} as Choice)
                                        }
                                    />

                                </Space>
                            </Flex>
                            <LayerList
                                data={props.narration.choices}
                                itemStyle={{}}
                                clickable={true}
                                title={(item, index) => item.title?.startsWith('http') ? <Image className={'choice-preview'} src={item.title}  height={24}/> : item.title ?? ''}
                                onItemClick={(item) => {

                                }}
                                onDeleteClick={(item) => {
                                    showConfirmChoiceDeletion(item)
                                }}
                                onEditClick={(item) => {
                                    setShowAddChoiceDialog(item)
                                }}
                            />
                        </div>
                    </Space>
                </Flex>
            </Modal>

            {showSelectNarratorDialog ?
                <NarratorManagerDialog
                    onNarratorSelected={(narrator: Narrator) => {
                        if (projectId && props.narration.id && narrator.id) {
                            dispatch(setNarrator({
                                projectId: parseInt(projectId),
                                narrationId: props.narration.id,
                                narratorId: narrator.id
                            }))
                            setShowSelectNarratorDialog(false)
                        }
                    }}
                    onCancel={() => setShowSelectNarratorDialog(false)}
                />
                : ''}
            {showAddChoiceDialog ?
                <ChoiceEditDialog
                    choice={showAddChoiceDialog}
                    onSave={(choice: Choice) => {
                        if (projectId && props.narration.id) {
                            dispatch(saveChoice({
                                projectId: parseInt(projectId),
                                narrationId: props.narration.id,
                                choice: choice
                            }))
                            setShowAddChoiceDialog(undefined)
                        }
                    }}
                    onCancel={() => setShowAddChoiceDialog(undefined)}
                />
                : ''}
        </>
    )
}